import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  HStack,
  SimpleGrid,
  FormErrorMessage,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { getCurrentDate } from "utils/dynDateUtils";
import { MOOD_EMOJI_MAP, WEATHER_EMOJI_MAP } from "utils/data/options";

const EmojiSelector = ({ map, selectedKey, onChange }) => {
  return (
    <SimpleGrid columns={{ base: 5, md: Object.keys(map).length }} spacing={2}>
      {Object.entries(map).map(([key, emoji]) => (
        <Box
          key={key}
          as="button"
          type="button"
          fontSize="2xl"
          borderWidth={selectedKey === key ? "2px" : "1px"}
          borderColor={selectedKey === key ? "teal.500" : "gray.300"}
          borderRadius="md"
          px={3}
          py={2}
          bg={selectedKey === key ? "teal.50" : "white"}
          _hover={{ borderColor: "teal.400" }}
          onClick={() => onChange(key)}
          textAlign="center"
        >
          {emoji}
        </Box>
      ))}
    </SimpleGrid>
  );
};

const DiaryForm = ({ data, updateDiary, isDisabled }) => {
  const validationSchema = Yup.object().shape({
    content: Yup.string()
      .min(100, "Diary must be at least 100 characters")
      .required("I know you have something to share."),
    title: Yup.string().required("Title is required"),
  });

  return (
    <Formik
      initialValues={{
        title: data?.title || "",
        mood: data?.mood || "happy",
        weather: data?.weather || "sunny",
        content: data?.content || "",
        diaryDate: data?.diaryDate || getCurrentDate(),
        submitType: data?.submitType || 1,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, actions) => {
        updateDiary(values);
        // actions.setSubmitting(false);
      }}
    >
      {({ values, setFieldValue, submitForm, errors, touched }) => (
        <Form>
          <FormControl
            isRequired
            isInvalid={errors.title && touched.title}
            mb={4}
          >
            <FormLabel>Title</FormLabel>
            <Field as={Input} name="title" placeholder="Enter a title..." />
            <FormErrorMessage>{errors.title}</FormErrorMessage>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Mood</FormLabel>
            <EmojiSelector
              map={MOOD_EMOJI_MAP}
              selectedKey={values.mood}
              onChange={(key) => setFieldValue("mood", key)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Weather</FormLabel>
            <EmojiSelector
              map={WEATHER_EMOJI_MAP}
              selectedKey={values.weather}
              onChange={(key) => setFieldValue("weather", key)}
            />
          </FormControl>

          <FormControl
            isRequired
            isInvalid={errors.content && touched.content}
            mb={4}
          >
            <FormLabel>Content</FormLabel>
            <Field
              as={Textarea}
              name="content"
              placeholder="Write your diary here..."
              rows={10}
            />
            <FormErrorMessage>{errors.content}</FormErrorMessage>
          </FormControl>

          <HStack justifyContent="flex-end" spacing={4}>
            <Button
              type="button"
              disabled={isDisabled}
              variant="outline"
              colorScheme="green"
              size="md"
              mb={8}
              onClick={async () => {
                await setFieldValue("submitType", 1);
                submitForm(); // triggers validation
              }}
            >
              Save
            </Button>
            <Button
              disabled={isDisabled}
              colorScheme="green"
              size="md"
              mb={8}
              onClick={async () => {
                await setFieldValue("submitType", 2);
                submitForm(); // triggers validation
              }}
            >
              Submit
            </Button>
          </HStack>
        </Form>
      )}
    </Formik>
  );
};

DiaryForm.propTypes = {
  updateDiary: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.string),
};

export default DiaryForm;
